import {
  Injectable,
  Inject,
  PLATFORM_ID,
  Optional,
  EventEmitter,
  InjectionToken,
  TransferState,
  makeStateKey,
  REQUEST_CONTEXT
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpContext } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { PlatformInformationInterface } from '../interfaces/platform-information.interface';
import { PlatformInformationConfigInterface } from '../interfaces/platform-information-config.interface';
import { catchError, tap } from 'rxjs/operators';
import { MulticastObserverFactoryService } from '../../../../../src/app/core/services/multicast-observer-factory.service';
import { MicrositeInterface } from '../interfaces/microsite.interface';
import { MicrositeapiInterface } from '../interfaces/micrositeapi.interface';
import { delayedRetry } from './DEFAULT_MAX_RETRIES';
import { SKIP_AB_TEST_HEADERS } from './platform-information-token';
import { REQUEST } from '@angular/core';
import { environment } from '../../../../../src/environments/environment';
export const BROWSER_WINDOW = new InjectionToken<Window>('Browser Window', {
  providedIn: 'root',
  factory: () => (typeof window !== 'undefined' ? window : null)
});

export class PlatformInformationServiceConfigurator implements PlatformInformationConfigInterface {
  hostname: string;
  payment_hostname: string;
  env: string;
  production: boolean;
  language: {
    selection_names: {
      de: string;
      en: string;
      fr: string;
      it: string;
      nl: string;
    };
  };
  api_endpoints: {
    getOffers: string;
    getPlatformInformation: string;
    getShops: {
      ar: string;
      localhost: string;
    };
    getTown: {
      ar: string;
      localhost: string;
    };
    getAutocomplete: string;
    getAutocompleteCoursePoi: string;
    getPromotionCode: string;
    getDiscountCountdowns: string;
    topcountries: string;
  };
  platformFallback?: string;
  platform: { [key: string]: PlatformInformationInterface };
  sales: any;
}

@Injectable({
  providedIn: 'root'
})
export class PlatformInformationService {
  partner?: string;
  selectionNames: { [key: string]: string };
  data$: Observable<PlatformInformationInterface>;
  customerCountryCode$: Observable<string>;
  microsite$: Observable<MicrositeInterface>;
  datMicro = new EventEmitter<MicrositeInterface>();
  isOfficeIp: boolean;

  private platformData?: { [key: string]: PlatformInformationInterface };
  private data: PlatformInformationInterface;
  private customerCountryCode: string;
  private isBrowser: boolean;
  private _micrositeConfig: MicrositeInterface;
  private headers = new HttpHeaders({ Accept: 'application/json' });

  get micrositeConfig(): MicrositeInterface | null {
    return this._micrositeConfig;
  }

  set micrositeConfig(micrositeConfig: MicrositeInterface) {
    this._micrositeConfig = micrositeConfig;
  }

  constructor(
    private client: HttpClient,
    public configuration: PlatformInformationServiceConfigurator,
    multicastObserverFactoryService: MulticastObserverFactoryService,
    @Inject(PLATFORM_ID) platformId: object,
    @Optional() @Inject(REQUEST) private req: Request,
    @Optional() @Inject(REQUEST_CONTEXT) private requestContext,
    @Optional() @Inject(BROWSER_WINDOW) protected window: Window,
    private transferState: TransferState
  ) {
    this.configuration.hostname = this.requestContext?.SSR_API_HOST;
    this.configuration.payment_hostname = this.requestContext?.SSR_PAYMENT_API_HOST;
    this.platformData = this.requestContext?.SSR_PLATFORM_DATA;
    this.isBrowser = isPlatformBrowser(platformId);
    this.selectionNames = this.configuration.language.selection_names;

    this.data$ = multicastObserverFactoryService.create<PlatformInformationInterface>(this.loadPlatformData);
    this.customerCountryCode$ = multicastObserverFactoryService.create<string>(this.loadCustomerCountryData);
    this.microsite$ = multicastObserverFactoryService.create<MicrositeInterface>(this.loadMicrositeConfig);
  }

  init() {
    const transferKey = makeStateKey<{ core: string; payment: string }>('platformApi');
    if (!this.isBrowser) {
      this.configuration.hostname = this.requestContext?.SSR_API_HOST;
      this.configuration.payment_hostname = this.requestContext?.SSR_PAYMENT_API_HOST;
      this.transferState.set(transferKey, {
        core: this.configuration.hostname,
        payment: this.configuration.payment_hostname
      });
    } else {
      if (this.transferState.hasKey(transferKey)) {
        const config = this.transferState.get(transferKey, null);
        this.configuration.hostname = config.core;
        this.configuration.payment_hostname = config.payment;
        return of(config);
      }
      return this.client
        .get<{ core: string; payment: string }>('/components/api-config', { transferCache: true })
        .pipe(
          catchError(error => {
            console.error(error);
            return of({
              core: environment.hostname?.length ? environment.hostname : 'https://www.alpy.com',
              payment: environment.payment_hostname?.length
                ? environment.payment_hostname
                : 'https://odin.alpy.com'
            });
          }),
          tap(res => {
            this.configuration.hostname = res.core;
            this.configuration.payment_hostname = res.payment;
          })
        );
    }
  }

  isLiveProd(): boolean {
    return (
      this.configuration.production &&
      (this.configuration.env === 'node' || this.configuration.env === 'cloudflare')
    );
  }

  isProductionEnvironment(): boolean {
    return this.configuration.production;
  }

  getIsBrowser(): boolean {
    return this.isBrowser;
  }

  getWindow(): Window {
    return this.window;
  }

  private loadCustomerCountryData = (observer?) => {
    if (this.customerCountryCode) {
      observer.next(this.customerCountryCode);
      observer.complete();
    } else {
      // load data from server and add it to the stream
      this.getCustomerCountryCode().subscribe({
        next: customerCountryCode => {
          this.customerCountryCode = customerCountryCode; // cache answer from server
          observer.next(this.customerCountryCode);
          observer.complete();
        },
        error: error => {
          console.error(error);
          observer.complete();
        }
      });
    }
  };

  // load platform config data form server and cache it locally
  private loadPlatformData = (observer?) => {
    if (this.data) {
      observer.next(this.data);
      observer.complete();
    } else {
      // load data from server and add it to the stream
      this.getPlatformInformation().subscribe({
        next: data => {
          this.data = data; // cache answer from server
          observer.next(this.data);
          observer.complete();
        },
        error: err => {
          console.error(err);
          observer.next(null);
          observer.complete();
        }
      });
    }
  };

  private loadMicrositeConfig = (observer?) => {
    if (this.micrositeConfig) {
      observer.next(this.micrositeConfig);
      observer.complete();
    } else {
      if (this.partner != null && this.partner != '') {
        this.getMicrositeConfig().subscribe({
          next: data => {
            this.micrositeConfig = data.microsites.find(
              (microsite: MicrositeInterface) => microsite.config.variation_prefix === this.partner
            );
            if (this.micrositeConfig) {
              if (this.micrositeConfig?.zopim && this.data) {
                this.data.zopim_key = this.micrositeConfig.zopim.key;
                this.data.zopim_enabled = this.micrositeConfig.zopim.enabled;
              }

              observer.next(this.micrositeConfig);
              this.datMicro.emit(this.micrositeConfig);
              observer.complete();
            } else {
              this.partner = null;
              this.micrositeConfig = null;
              observer.complete();
            }
          },
          error: err => {
            console.error(err);
            observer.complete();
          }
        });
      }
    }
  };

  private getCustomerCountryCode(): Observable<string> {
    if (!this.isBrowser) {
      return of('AT');
    }
    const url = this.getApiEndpointFor('getCustomerGeoIpCountryCode');
    return this.client
      .get(url, {
        context: new HttpContext().set(SKIP_AB_TEST_HEADERS, true),
        responseType: 'text'
      })
      .pipe(
        delayedRetry(8000, 2),
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          return throwError(() => new Error('Could not load customer-country-code: ' + url));
        })
      );
  }

  /**
   * returns an observable of the platform information
   */
  private getPlatformInformation(): Observable<PlatformInformationInterface> {
    const partner = this.getCurrentPartner() ?? '';
    return this.getPlatfromObservable().pipe(
      tap(platformInfo => {
        // 'notnagel' because backend can't provide partner specific hotline for api call
        if (partner === 'partner-skiworld') {
          platformInfo.hotlines = {
            de: '+43 6456 7682',
            en: '+43 6456 7682',
            fr: '+43 6456 7682',
            it: '+43 6456 7682',
            nl: '+43 6456 7682',
            sk: '+43 6456 7682',
            cs: '+43 6456 7682',
            pl: '+43 6456 7682',
            es: '+43 6456 7682',
            da: '+43 6456 7682'
          };
          platformInfo.info_email = 'shop@skiworld.at';
        }
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(
          () => new Error('Could not load platform data. Error: ' + error.status + error.message)
        );
      })
    );
  }

  /**
   * provides correct subscribable for platform data based on env
   * @returns
   */
  private getPlatfromObservable(): Observable<PlatformInformationInterface> {
    const partner = this.getCurrentPartner() ?? '';
    const url = this.getApiEndpointFor('getPlatformInformation')
      .replace('%partner%/', partner ? partner + '/' : '')
      .replace('%lang%', this.getDefaultLangForPlatform());
    const transferKey = makeStateKey<PlatformInformationInterface>('getPlatformInformation'); // match a state for the url request

    // on ssr and platformdata has data? use it
    if (this.platformData && this.platformData[partner ? partner : this.getCurrentPlatformShortcode()]) {
      const value = this.platformData[partner ? partner : this.getCurrentPlatformShortcode()];
      this.transferState.set(transferKey, value);
      return of(value);
    }

    // if (anywhere) has the transferState? use it
    if (this.transferState.hasKey(transferKey)) {
      const value = this.transferState.get(transferKey, null);
      this.transferState.remove(transferKey);
      return of(value);
    }

    return this.client.get<PlatformInformationInterface>(url, { headers: this.headers });
  }

  private getMicrositeConfig(): Observable<MicrositeapiInterface> {
    const url = this.getApiEndpointFor('getMicrositeConfigApi').replace(
      '%lang%',
      this.getDefaultLangForPlatform()
    );

    return this.client.get<MicrositeapiInterface>(url, { headers: this.headers }).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(
          () => new Error('Could not load microsite data: ' + url + ' Error: ' + error.status + error.message)
        );
      })
    );
  }

  getDefaultLangForPlatform(): string {
    switch (this.getCurrentPlatformShortcode()) {
      case 'pf':
        return 'de';
      case 'mc':
        return 'fr';
      case 'df':
        return 'fr';
      case 'sm':
        return 'fr';
      case 's2':
        return 'fr';
      case 'es':
        return 'fr';
      default:
        return 'en';
    }
  }

  getAvailableLanguagesForPlatform(): string[] {
    // get all available languages. By fallback, config or shortcode
    let lang = ['en', 'de', 'fr', 'it', 'nl', 'sk', 'cs', 'pl', 'es', 'da'];
    const platLangs = this.configuration?.platform[this.getCurrentPlatformShortcode()]?.acceptedLanguages;
    if (platLangs) {
      lang = platLangs;
    }
    // if path is requested with a defined lang, sort it to first place
    const lg = this.req?.url ? new URL(this.req.url).pathname.slice(1, 3) : this.getDefaultLangForPlatform();
    if (lang.includes(lg)) {
      lang.splice(lang.indexOf(lg), 1);
      lang.splice(0, 0, lg);
    }
    return lang;
  }

  getCurrentPartner(): string | null {
    try {
      if (this.partner) {
        return this.partner;
      } else {
        const regex = /partner\-[a-zA-Z0-9-]*/;

        const partner = this.window
          ? this.window.location.pathname.match(regex)
          : this.req?.url
            ? new URL(this.req.url).pathname.match(regex)
            : [];
        if (partner) {
          this.partner = partner[0];
          return partner[0];
        }
      }
    } catch {
      return null;
    }
    return null;
  }

  /**
   * returns the 'base api' (core) host to use to communicate with core.
   * Also used to generate asset urls that are based in the backend.
   * @param lang
   * @returns
   */
  getHostname(lang?: string): string {
    let host = this.configuration?.hostname ?? this.window?.location?.hostname;
    host = host ?? '';
    if (lang) {
      return host + '/' + lang;
    }
    return host;
  }

  /**
   * this (angular) origin on ssr and browser
   * @returns up to https://www.xxx.yyy:8787
   */
  getOrigin(): string {
    if (this.isBrowser) {
      return this.window.location.origin;
    }
    return new URL(this.req.url).origin;
  }

  /**
   * Tells you which satellite behavior / styles should be used
   * @returns the platform shortcode based on the current location / request.
   */
  getCurrentPlatformShortcode(): string {
    const hostname = this.req?.url ?? this.window?.location?.hostname;
    if (hostname) {
      if (hostname.includes('alpinresorts') || hostname.includes('alpy.com')) {
        return 'ar';
      }
      if (hostname.includes('simplytoski.fr')) {
        return 's2';
      }
      if (hostname.includes('snowbrainer')) {
        return 'sb';
      }
      if (hostname.includes('slopefox')) {
        return 'sf';
      }
      if (hostname.includes('pistenfuchs')) {
        return 'pf';
      }
      if (hostname.includes('skimarie')) {
        return 'sm';
      }
      if (hostname.includes('location-ski-moins-cher')) {
        return 'mc';
      }
      if (hostname.includes('skidiscount.fr')) {
        return 'df';
      }
      if (hostname.includes('skidiscount.co')) {
        return 'du';
      }
      if (hostname.includes('best-price-ski-rental')) {
        return 'bp';
      }
    }
    return this.configuration.platformFallback;
  }

  getPlatformName(): string {
    switch (this.getCurrentPlatformShortcode()) {
      case 'ar':
        return 'www.ALPY.com';
      case 'sb':
        return 'www.SNOWBRAINER.com';
      case 'sf':
        return 'www.SLOPEFOX.co.uk';
      case 'pf':
        return 'www.PISTENFUCHS.de';
      case 'sm':
        return 'www.SKIMARIE.fr';
      case 'mc':
        return 'www.LOCATION-SKI-MOINS-CHER.com';
      case 'df':
        return 'www.SKIDISCOUNT.fr';
      case 'du':
        return 'www.SKIDISCOUNT.co.uk';
      case 'bp':
        return 'www.BEST-PRICE-SKI-RENTAL.com';
      case 's2':
        return 'www.SIMPLYTOSKI.fr';
      default:
        return '';
    }
  }

  getAnalyticsContainer(): string {
    if (!this.isLiveProd()) {
      return 'G-FMM020TLZ4';
    }
    switch (this.getCurrentPlatformShortcode()) {
      case 'sm': // skimarie
        return 'UA-34821831-1';
      case 's2': // simplytoski
        return 'G-T7XR45KR7W';
      case 'sf': // slopefox
        return 'G-3K02KLPV13';
      case 'sb': // snowbrainer
        return 'G-EK5F3GV885';
      case 'pf': // pistenfuchs
        return 'G-ZSMQR740SJ';
      case 'mc': // moins-cher
        return 'G-HEVJFT3G6D';
      case 'bp': // best-price
        return 'G-BNCZMK2P27';
      case 'du': // skidiscount.uk
        return 'G-M6EPEJ2HTD';
      case 'df': // skidiscount.fr
        return 'G-PGG84R4N6N';
      default: // Alpy.com
        return 'G-K227T8B85T';
    }
  }

  getTagManagerId(): string {
    // beta gets its own tagmanager
    if (!this.isLiveProd()) {
      return 'GTM-W4XX2RM';
    }
    switch (this.getCurrentPlatformShortcode()) {
      case 'sm': // skimarie.fr
        return 'GTM-NKL9QXF';
      case 's2': // simplytoski.fr
        return 'GTM-KDTWKTT';
      case 'sf': // slopefox.co.uk
        return 'GTM-NBKLZM3';
      case 'sb': // snowbrainer.com
        return 'GTM-WLLQCDP';
      case 'pf': // pistenfuchs.de
        return 'GTM-TVWCKMB';
      case 'mc': // location-ski-moins-cher.com
        return 'GTM-M5LCVXP';
      case 'bp': // best-price-ski-rental
        return 'GTM-KDSTQ2H';
      case 'du': // skidiscount.uk
        return 'GTM-WGLSFKR';
      case 'df': // skidiscount.fr
        return 'GTM-TDFBJZM';
      default: // alpy.com
        return 'GTM-KJ6QNNR';
    }
  }

  getApiEndpointFor(name: string): string {
    const api: string = this.configuration.api_endpoints[name];
    if (api) {
      if (api.startsWith('/')) {
        if (this.isBrowser) {
          return api;
        }
        const url = new URL(this.req.url).origin;
        return url + api;
      }
      return api.startsWith('http') ? api : `${this.getHostname()}/${api}`;
    }
    console.error(`No config for API endpoint named ${name} found!`);
    return '';
  }
}
